import { LayoutGrid, List } from 'lucide-react';
import { useEffect, useState } from 'react';

export type Visao = 'lista' | 'cards';

const CHAVE = 'social.visao';

/**
 * Lista ou cards, LEMBRADO por tela.
 *
 * As duas visões existem porque as duas leituras existem: a lista compara
 * (quem entrega antes? qual custa mais?) e o card identifica (é este sofá
 * mesmo?). Quem confere pedido quer lista; quem escolhe produto quer card.
 *
 * A preferência é guardada por tela e não global: a mesma pessoa quer lista na
 * consulta de pedidos e card no seletor de produto, e uma preferência única
 * obrigaria a trocar toda vez que mudasse de tela.
 *
 * No CELULAR a lista vira card sozinha — uma tabela de oito colunas numa tela
 * de 375px não é uma lista, é um scroll horizontal. Por isso o alternador só
 * aparece a partir do `sm`.
 */
export function useVisao(tela: string): [Visao, (v: Visao) => void] {
  const [visao, setVisao] = useState<Visao>('lista');

  useEffect(() => {
    const guardada = localStorage.getItem(`${CHAVE}.${tela}`);
    if (guardada === 'lista' || guardada === 'cards') setVisao(guardada);
  }, [tela]);

  const trocar = (v: Visao) => {
    setVisao(v);
    localStorage.setItem(`${CHAVE}.${tela}`, v);
  };

  return [visao, trocar];
}

export function AlternadorVisao({
  visao,
  aoTrocar,
}: {
  visao: Visao;
  aoTrocar: (v: Visao) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Modo de visualização"
      /*
        `h-11` no INVÓLUCRO, e o filho em `h-full`.

        A estrutura (invólucro com `p-0.5` e pastilha por dentro) é a do
        isotec e fica — é ela que dá o respiro e o alvo certo. O que não podia
        ficar era a altura vindo de baixo para cima: com o filho em `h-10`, o
        conjunto fechava em 42,85px contra os 41,25px de TODO o resto da
        barra, e o controle sobrava 1,6px sobre a linha de base. Um item fora
        de esquadro numa barra em que tudo mais bate é o "desalinhado" que se
        vê antes de saber o que é.
      */
      className="border-tinta-100 bg-campo hidden h-11 rounded-lg border p-0.5 sm:inline-flex"
    >
      {[
        { id: 'lista' as const, rotulo: 'Lista', icone: List },
        { id: 'cards' as const, rotulo: 'Cards', icone: LayoutGrid },
      ].map(({ id, rotulo, icone: Icone }) => (
        <button
          key={id}
          type="button"
          aria-label={rotulo}
          title={rotulo}
          aria-pressed={visao === id}
          onClick={() => aoTrocar(id)}
          className={`inline-flex h-full w-10 items-center justify-center rounded transition-colors ${
            visao === id
              ? 'bg-marca-600/10 text-marca-600'
              : 'text-tinta-500 hover:bg-marca-600/[0.07] hover:text-marca-600'
          }`}
        >
          <Icone className="size-4" aria-hidden />
        </button>
      ))}
    </div>
  );
}
